import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { Snackbar } from '@host/components/NewUI/ComponentsLibrary';
import { useSingleRoot } from '@host/components/NewUI/SingleRoot';

import { registerSnackbarNotifier } from './snackbarBus';

import type { ReactNode } from 'react';

import type { SnackbarInput, SnackbarState } from '@host/components/NewUI/ComponentsLibrary';

interface SnackbarContextValue {
  /** Show a snackbar with an explicit state (defaults to 'success'). */
  showSnackbar: (message: string, state?: SnackbarState) => void;
  showSuccess: (message: string) => void;
  showWarning: (message: string) => void;
  showError: (message: string) => void;
}

const SnackbarContext = createContext<SnackbarContextValue | null>(null);

export const useSnackbar = (): SnackbarContextValue => {
  const ctx = useContext(SnackbarContext);
  if (!ctx) {
    throw new Error('useSnackbar must be used within an OnboardSnackbarProvider');
  }
  return ctx;
};

interface Props {
  children: ReactNode;
}

// Single snackbar host for an onboard SPA. Renders the shared design-system
// Snackbar and exposes `useSnackbar()` for opt-in success/warning messages.
// Errors from React Query are routed here automatically via the snackbar bus
// (see OnboardProviders.tsx + snackbarBus.ts). Messages that arrive while one is already
// visible are queued and shown in turn rather than dropped. Inside the single-root
// shell, messages go to the shell's one snackbar instead.
const OnboardSnackbarProvider = ({ children }: Props) => {
  const [current, setCurrent] = useState<SnackbarInput | null>(null);
  const queueRef = useRef<SnackbarInput[]>([]);
  const shellNotify = useSingleRoot()?.notify;

  const showSnackbar = useCallback(
    (message: string, state: SnackbarState = 'success') => {
      if (!message) return;
      if (shellNotify) {
        shellNotify({ message, state });
        return;
      }
      setCurrent((existing) => {
        if (existing) {
          queueRef.current.push({ message, state });
          return existing;
        }
        return { message, state };
      });
    },
    [shellNotify]
  );

  const handleClose = useCallback(() => {
    setCurrent(queueRef.current.shift() ?? null);
  }, []);

  // Bridge non-React callers (React Query cache, plain async helpers) to this host.
  useEffect(() => {
    registerSnackbarNotifier(showSnackbar);
    return () => registerSnackbarNotifier(null);
  }, [showSnackbar]);

  const value = useMemo<SnackbarContextValue>(
    () => ({
      showSnackbar,
      showSuccess: (message: string) => showSnackbar(message, 'success'),
      showWarning: (message: string) => showSnackbar(message, 'warning'),
      showError: (message: string) => showSnackbar(message, 'error')
    }),
    [showSnackbar]
  );

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      {!shellNotify && (
        <Snackbar
          message={current?.message ?? ''}
          onClose={handleClose}
          state={current?.state ?? 'success'}
        />
      )}
    </SnackbarContext.Provider>
  );
};

export default OnboardSnackbarProvider;
